import { FC } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Box } from '@mui/material';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';
import { useScrollTo } from 'hooks/useScrollTo';

import { useGlobalProducts } from 'components/BusinessToolForm/hooks/useGlobalProducts';
import { GLOBAL_PRODUCTS_NAME } from 'components/BusinessToolForm/Sections/Global/form';
import { PRODUCT_NAMES } from 'components/BusinessToolForm/Sections/Global/general/products';

import { concatWithDot } from 'helpers/string';

import { EachUnitFieldsList } from './components/EachUnitFieldsList/EachUnitFieldsList';
import { EachUnitTable } from './components/EachUnitTable/EachUnitTable';
import { COST_OF_EACH_UNIT_FORM_NAMES, defaultCostOfEachUnitItem } from './utils/form';
import { costOfEachUnitPrefix } from '../utils/form';

export const CostOfEachUnitForm: FC = () => {
  const { control } = useFormContext();

  const [elementRef, scrollToElement] = useScrollTo();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.PLANNED_EXPENSES,
  );

  const { handleAddProduct, handleRemoveProduct, products } = useGlobalProducts();

  const tableName = concatWithDot(costOfEachUnitPrefix, COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_TABLE);

  const {
    fields: tableRows,
    remove: removeTableRow,
    insert: insertTableRow,
  } = useFieldArray({ name: tableName, control });

  const removeTableRowHandler = (index: number) => removeTableRow(index);
  const insertTableRowHandler = (index: number) => insertTableRow(index, defaultCostOfEachUnitItem);

  const watchColumnTitleName = concatWithDot(GLOBAL_PRODUCTS_NAME, ':index', PRODUCT_NAMES.NAME);

  return (
    <Box>
      <Box ref={elementRef} />
      <EachUnitFieldsList
        name={GLOBAL_PRODUCTS_NAME}
        fields={products}
        disabled={!canAccess}
        onAddRow={handleAddProduct}
        onRemoveRow={handleRemoveProduct}
      />

      <EachUnitTable
        name={tableName}
        fields={tableRows}
        columns={products}
        disabled={!canAccess}
        watchColumnTitleName={watchColumnTitleName}
        onRemoveRow={removeTableRowHandler}
        onInsertRow={insertTableRowHandler}
        scrollToElement={scrollToElement}
      />
    </Box>
  );
};
